import { api } from '@organization/api'
import {
  LOGIN_URL,
  messages,
  PASSWORD_PATTERN
} from '@organization/app-constants'
import { AuthWrapper } from '@organization/features/authentication/components/AuthWrapper'
import { useSearchParams } from '@organization/hooks'
import { useMutation } from '@tanstack/react-query'
import { Alert, App, Button, Card, Flex, Form, Input, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useStyle } from '../components/AuthWrapper/styles'

export const ResetPasswordConfirmForm = () => {
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const { modal } = App.useApp()
  const { t } = useTranslation()

  const params = useSearchParams()

  const token = params.get('token')
  const { styles } = useStyle()

  const onSubmit = async () => {
    const values = await form.validateFields()

    mutate({
      password: values.password,
      confirm_password: values.confirm_password,
      token: String(token)
    })
  }

  const { mutate, error, isPending } = useMutation({
    mutationFn: api.account.confirmRestore,
    onSuccess: () => {
      modal.success({
        title: messages.successResetPassword,
        onOk: () => navigate(LOGIN_URL)
      })
    }
  })

  return (
    <AuthWrapper>
      <Card className={styles.card}>
        <Flex vertical gap={16}>
          <Typography.Title level={4}>
            {t('auth.reset_password_header')}
          </Typography.Title>
          <Typography.Text>
            {t('auth.reset_password_description')}
          </Typography.Text>
          {error?.message && (
            <Alert type="error" message={error.message} showIcon />
          )}
        </Flex>

        <Form
          form={form}
          layout="vertical"
          className={styles.form}
          requiredMark={false}
        >
          <Form.Item
            label={t('form.item.new_password')}
            name="password"
            rules={[
              {
                pattern: PASSWORD_PATTERN,
                message: t('form.message.new_password_regex')
              },
              { required: true, message: t('form.message.password_required') }
            ]}
          >
            <Input.Password placeholder={t('form.placeholder.new_password')} />
          </Form.Item>

          <Form.Item
            label={t('form.item.confirm_password')}
            name="confirm_password"
            dependencies={['password']}
            rules={[
              {
                pattern: PASSWORD_PATTERN,
                message: t('form.message.new_password_regex')
              },
              {
                required: true,
                message: t('form.message.confirm_password')
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve()
                  }
                  return Promise.reject(new Error(t('error.password_no_match')))
                }
              })
            ]}
          >
            <Input.Password
              placeholder={t('form.placeholder.confirm_password')}
            />
          </Form.Item>

          <Button block type="primary" loading={isPending} onClick={onSubmit}>
            {t('button.submit')}
          </Button>
        </Form>
      </Card>
    </AuthWrapper>
  )
}
